/**
 * A dedicated stable-DB feed for the alerts engine, independent of the table.
 *
 * Alerts used to see only the underlyings the user happened to be fetching for the
 * table, so an alert on SPY was silent unless SPY was on screen. This feed always
 * pulls a priority set (SPY, QQQ, SPXW) plus every underlying an active stable-fetch
 * alert references, so those rules always have rows to evaluate — no matter what the
 * table is showing. Runs while the app is open (the alerts engine is client-side).
 */
import { useEffect, useRef, useState } from 'react';
import { dataService } from '../services/dataService';
import type { AlertNode, AlertRule } from '../utils/alertRules';
import { STABLE_FETCH_SOURCE } from '../utils/alertSources';

/** Always tracked for alerts, regardless of the table selection. */
export const ALERT_PRIORITY_UNDERLYINGS = ['SPY', 'QQQ', 'SPXW'];
const POLL_MS = 30_000;

function collectUnderlyings(node: AlertNode, out: Set<string>): void {
  if (node.kind === 'condition') {
    // Underlyings a rule targets: `Underlying is X` / `Underlying contains X`.
    if (node.field === 'Underlying' && (node.op === 'eq' || node.op === 'contains')) {
      const v = String(node.value ?? '').trim().toUpperCase();
      if (v) out.add(v);
    }
    return;
  }
  node.children.forEach((c) => collectUnderlyings(c, out));
}

/**
 * Priority set ∪ underlyings named by active stable-fetch alert rules. Empty when no
 * stable-fetch alert is active, so the feed doesn't poll when there's nothing to fire.
 */
export function alertUnderlyings(rules: AlertRule[]): string[] {
  const active = rules.filter((r) => r.enabled && r.sourceId === STABLE_FETCH_SOURCE.id);
  if (!active.length) return [];
  const set = new Set<string>(ALERT_PRIORITY_UNDERLYINGS);
  for (const r of active) collectUnderlyings(r.root, set);
  return Array.from(set);
}

/** Poll the stable chain for the alert underlyings; returns the latest rows. */
export function useAlertUnderlyingsFeed(rules: AlertRule[]): Record<string, unknown>[] {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const symsKey = alertUnderlyings(rules).sort().join(',');
  const acRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const syms = symsKey ? symsKey.split(',') : [];
    if (!syms.length) {
      setRows([]);
      return;
    }
    let stopped = false;
    const fetchOnce = async () => {
      acRef.current?.abort();
      const ac = new AbortController();
      acRef.current = ac;
      try {
        const data = await dataService.fetchRawData('accumulated', ac.signal, {
          sourceOverride: 'stable',
          underlyingsOverride: syms,
          // Alerts want every strike (so a spike on any strike fires) across the full
          // 6-month expiry span. The 212 warm table now covers that span: a fast worker
          // keeps the near-term (0/1DTE + this week) fresh every ~60s while a separate deep
          // worker sweeps the whole 6 months into the same table one expiry at a time. The
          // endpoint post-filters warm rows to the requested expiry range, so the feed has
          // to ask for '6-months' to actually receive the far-dated contracts.
          strikeRangeOverride: 'all',
          expiryRangeOverride: '6-months',
        });
        if (!stopped) setRows(Array.isArray(data) ? (data as unknown as Record<string, unknown>[]) : []);
      } catch {
        /* keep the last good rows */
      }
    };
    fetchOnce();
    const id = window.setInterval(fetchOnce, POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(id);
      acRef.current?.abort();
    };
  }, [symsKey]);

  return rows;
}
